import { createStore, thunk, action, computed } from 'easy-peasy';
import * as topojson from 'topojson';
import fetch from 'unfetch';
import { csvParse } from 'd3-dsv';
import { scaleQuantile } from 'd3-scale';

import { isMobile } from '~/core/scripts/utils';

import {
  getResultData,
  getOutlineConcave,
  getPresetResult,
  dissolve,
} from './utils';
import config from './config';

async function getTopoJson(url) {
  const res = await fetch(url);
  const topo = await res.json();
  const topoKey = Object.keys(topo.objects)[0];
  const topoObject = topo.objects[topoKey];

  topoObject.geometries = topoObject.geometries.map((geom) => {
    geom.properties.__id = geom.properties.Wahlgebiet_ID;
    return geom;
  });

  const geo = topojson.feature(topo, topoObject);

  return {
    topo,
    geo,
  };
}

function mergeData(geojson, data) {
  const dataMap = new Map(data.map((d) => [d.__id, d]));

  geojson.features = geojson.features.map((feat) => {
    const d = dataMap.get(feat.properties.__id);

    feat.properties = {
      ...feat.properties,
      ...d,
    };

    return feat;
  });

  return geojson;
}

function parseCsvRow(row) {
  row.__id = row.Wahlgebiet_ID;
  return row;
}

async function getCsv(url) {
  const res = await fetch(url);
  const csv = await res.text();
  const data = csvParse(csv, parseCsvRow);

  return data;
}

const errorMessages = {
  errorFeatureCount: 'Die Karte muss in genau zwei Teile zerschnitten werden.',
  errorShortLine: 'Die Linie ist zu kurz.',
  default: 'Da ist etwas schiefgelaufen. Bitte noch einmal versuchen.',
};

const Store = createStore({
  app: {
    indicators: config.indicators,
    selectedIndicator: config.indicators[0],
    isDrawingEnabled: true,
    showDrawEnableOverlay: isMobile,
    isDrawing: false,
    outlineTopoJson: null,
    outlineGeoJson: null,
    kreiseTopoJson: null,
    kreiseGeoJson: null,
    statesGeoJson: null,
    data: null,
    drawingError: null,
    dataLoading: true,
    resultData: null,
    drawCoords: [],
    kreisSearched: null,
    kreisSearchValue: null,
    drawingErrorMessage: computed(
      [(state) => state.drawingError],
      (drawingError) => {
        if (!drawingError || !drawingError.message) {
          return null;
        }

        return errorMessages[drawingError.message] || errorMessages.default;
      }
    ),
    currentValues: computed(
      [(state) => state.data, (state) => state.selectedIndicator],
      (data, selectedIndicator) => {
        if (!data) {
          return [0, 0];
        }
        return data.map((d) => selectedIndicator.calculateValue([d]));
      }
    ),
    colorScale: computed(
      [(state) => state.currentValues, (state) => state.selectedIndicator],
      (currentValues, selectedIndicator) => {
        return scaleQuantile()
          .domain(currentValues)
          .range(selectedIndicator.colors);
      }
    ),
    mapColorizer: computed(
      [(state) => state.selectedIndicator, (state) => state.colorScale],
      (selectedIndicator, colorScale) => {
        return (feat) => {
          const value = selectedIndicator.calculateValue([feat.properties]); // +feat.properties[selectedIndicator.key];

          if (Number.isNaN(value)) {
            return '#ddd';
          }

          return colorScale(value);
        };
      }
    ),
    loadDataSuccess: action((state, payload) => {
      state.outlineGeoJson = payload.outline;
      state.kreiseTopoJson = payload.kreiseTopo;
      state.kreiseGeoJson = payload.kreiseGeo;
      state.statesGeoJson = payload.statesGeoJson;
      state.data = payload.data;
      state.dataLoading = false;
    }),
    setDrawingError: action((state, payload) => {
      state.drawingError = payload;
    }),
    loadData: thunk(async (actions) => {
      const kreise = await getTopoJson('data/kreise_simpl.topo.json');
      const data = await getCsv('data/data.csv');
      const outline = getOutlineConcave(kreise.geo, 1.5);
      const kreiseMerged = mergeData(kreise.geo, data);
      const statesGeoJson = dissolve(kreise.topo, 'bl');

      actions.loadDataSuccess({
        outline,
        kreiseGeo: kreiseMerged,
        kreiseTopo: kreise.topo,
        data,
        statesGeoJson,
      });
    }),
    addDrawCoord: action((state, payload) => {
      state.drawCoords = state.drawCoords.concat(payload);
    }),
    resetDrawCoords: action((state) => {
      state.drawCoords = [];
    }),
    onResultData: action((state, payload) => {
      state.resultData = payload;
      state.isDrawing = false;
      state.isDrawingEnabled = false;
    }),
    setDrawing: action((state, payload) => {
      state.isDrawing = payload;
    }),
    setDrawingEnabled: action((state, payload) => {
      state.isDrawingEnabled = payload;
    }),
    setDrawEnableOverlay: action((state, payload) => {
      state.showDrawEnableOverlay = payload;
    }),
    finishDrawing: thunk(async (actions, payload, helpers) => {
      actions.setDrawingEnabled(false);
      try {
        const result = getResultData(helpers.getState());

        if (result) {
          actions.onResultData(result);
          actions.setDrawingError(null);
        } else {
          actions.restartDrawing();
        }
      } catch (err) {
        actions.setDrawingError(err);
        actions.restartDrawing();
      }
    }),
    setResultFromPreset: thunk(async (actions, payload, helpers) => {
      const result = getPresetResult(payload, helpers.getState());
      actions.onResultData(result);
    }),
    restartDrawing: action((state) => {
      state.isDrawingEnabled = true;
      state.isDrawing = false;
      state.drawCoords = [];
      state.resultData = null;
    }),
    setIndicator: action((state, payload) => {
      state.selectedIndicator = payload;
    }),
    setKreisSearched: action((state, payload) => {
      state.kreisSearched = payload;
    }),
    setKreisSearchValue: action((state, payload) => {
      state.kreisSearchValue = payload;
    }),
  },
  tooltip: {
    position: { x: 0, y: 0 },
    data: null,
    isVisible: false,
    isStatic: false,
    setPosition: action((state, payload) => {
      state.position = payload;
    }),
    setData: action((state, payload) => {
      state.data = payload;
    }),
    setVisible: action((state, payload) => {
      state.isVisible = payload;
    }),
    setStatic: action((state, payload) => {
      state.isStatic = payload;
    }),
  },
});

export default Store;
